import { useEffect } from "react";

import dayjs from "dayjs";

import { getOperatorRequests, getRequests as getExternalRequests } from "@synthesis/shared-services/external/application/client";
import type {
  GetOperatorRequestsProcessingFlagEnumKey,
  GetOperatorRequestsRailwayEnumKey,
  GetOperatorRequestsStatusEnumKey,
  GetRequestsRailwayEnumKey as ExternalGetRequestsRailwayEnumKey,
  GetRequestsRequestSubjectEnumKey as ExternalGetRequestsRequestSubjectEnumKey,
  GetRequestsStatusEnumKey,
} from "@synthesis/shared-services/external/application/types";
import { getRequests as getInternalRequests } from "@synthesis/shared-services/internal/application/client";
import type {
  GetRequestsDeadlineControlEnumKey,
  GetRequestsPaymentFlagEnumKey,
  GetRequestsProcessingFlagEnumKey,
  GetRequestsRequestSubjectEnumKey,
  InternalRequestDetailResponseRailwayEnumKey,
  InternalRequestDetailResponseStatusEnumKey,
} from "@synthesis/shared-services/internal/application/types";
import type { DateRangePickerValue, TableListColumnFilterModel } from "@synthesis/shared-ui";
import { useSearch } from "@synthesis/shared-utils";
import { parseAsString, useQueryState } from "nuqs";
import { getMonitoringRequestFilters } from "../../../shared/utils/monitoring-filters";
import { getMonitoringRailwayQuery, resolveMonitoringRailway } from "../../monitoring-page/monitoring-page.lib";
import { useUserInfo } from "../../../shared/hooks/use-user-info";

import { useClientType } from "../../../shared/hooks/use-client-type";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import {
  COLUMN_SORT_FIELD_MAP,
  EXTERNAL_APPLICANT_SORT_FIELD_MAP,
  EXTERNAL_OPERATOR_SORT_FIELD_MAP,
  TABLE_ROWS_PER_PAGE,
  fetchApplicationsTableRows,
  getPaymentFilterSelectedIds,
  mapPaymentFlagFilterIdsToQuery,
  matchesPaymentFlagFilter,
  getSelectedListFilterIds,
  getServerStatusFilter,
  mapAssigneeFilterIdsToQuery,
  toNumber,
  usePageStore,
  type ApplicationsTableItem,
} from "../applications-page.lib";

const getDateRangeQuery = (model: DateRangePickerValue | undefined): { from?: string; to?: string } => {
  if (model?.start == null || model?.end == null) {
    return {};
  }

  return {
    from: dayjs.unix(model.start).toISOString(),
    to: dayjs.unix(model.end).toISOString(),
  };
};

/** reviewDueFrom/To — ISO-даты (YYYY-MM-DD), колонка на бэке без времени */
const getDateOnlyRangeQuery = (model: DateRangePickerValue | undefined): { from?: string; to?: string } => {
  if (model?.start == null || model?.end == null) {
    return {};
  }

  return {
    from: dayjs.unix(model.start).format("YYYY-MM-DD"),
    to: dayjs.unix(model.end).format("YYYY-MM-DD"),
  };
};

export const useApplicationsTableDatasource = () => {
  const pageStore = usePageStore();
  const clientType = useClientType();
  const userRoles = useUserRoles();
  const [search] = useSearch();
  const [monitoringMetric] = useQueryState("monitoringMetric", parseAsString);
  const [monitoringRailway] = useQueryState("railway", parseAsString);
  const userInfo = useUserInfo();
  const isRailwayRestricted = userRoles.isInternalNtpChief || userRoles.isInternalApparatusNg;
  const scopedRailway = isRailwayRestricted ? resolveMonitoringRailway(userInfo?.railwayCandidates ?? [])?.id : monitoringRailway;

  useEffect(() => {
    const abortController = new AbortController();

    pageStore?.tableApi?.setGridOption("datasource", {
      getRows: async ({ failCallback, successCallback, startRow, endRow, sortModel, filterModel }) => {
        try {
          const size = Math.max(endRow - startRow, TABLE_ROWS_PER_PAGE);
          const searchQuery = search || undefined;
          const tableFilter = pageStore?.tableFilter ?? { type: "none" as const };
          const isExternalOperator = clientType === "external" && userRoles.isExternalOperator;
          const serverStatusFilter = getServerStatusFilter(tableFilter, { isExternalOperator });

          const sortColumn = sortModel?.[0];
          const sortDir = sortColumn?.colId ? (sortColumn?.sort === "desc" ? ("DESC" as const) : ("ASC" as const)) : undefined;

          const statusColumnIds = getSelectedListFilterIds(filterModel?.status as TableListColumnFilterModel | undefined);
          const columnStatusFilter = statusColumnIds.length > 0 ? statusColumnIds : undefined;
          const paymentFilterIds = getPaymentFilterSelectedIds(filterModel ?? undefined);
          const paymentRowMatcher =
            clientType === "external" && paymentFilterIds.length > 0
              ? (item: ApplicationsTableItem) => matchesPaymentFlagFilter(item, paymentFilterIds)
              : undefined;

          if (clientType === "internal") {
            const railway = getSelectedListFilterIds(filterModel?.railway as TableListColumnFilterModel | undefined) as
              | InternalRequestDetailResponseRailwayEnumKey[]
              | undefined;

            const requestSubject = getSelectedListFilterIds(filterModel?.type as TableListColumnFilterModel | undefined) as
              | GetRequestsRequestSubjectEnumKey[]
              | undefined;

            const status =
              statusColumnIds.length > 0
                ? (statusColumnIds as InternalRequestDetailResponseStatusEnumKey[])
                : (serverStatusFilter as InternalRequestDetailResponseStatusEnumKey[] | undefined);

            const sections = getSelectedListFilterIds(filterModel?.section as TableListColumnFilterModel | undefined);
            const applicantNames = getSelectedListFilterIds(filterModel?.applicantName as TableListColumnFilterModel | undefined);
            const initiatorIds = getSelectedListFilterIds(filterModel?.initiator as TableListColumnFilterModel | undefined);
            const { assigneeIds, assigneeUnassigned } = mapAssigneeFilterIdsToQuery(
              getSelectedListFilterIds(filterModel?.responsible as TableListColumnFilterModel | undefined),
            );
            const operatorIds = getSelectedListFilterIds(filterModel?.operator as TableListColumnFilterModel | undefined);
            const paymentFlag = mapPaymentFlagFilterIdsToQuery(paymentFilterIds) as GetRequestsPaymentFlagEnumKey[] | undefined;
            const processingFlag = getSelectedListFilterIds(filterModel?.processingFlag as TableListColumnFilterModel | undefined) as
              | GetRequestsProcessingFlagEnumKey[]
              | undefined;
            const deadlineControl = getSelectedListFilterIds(filterModel?.deadlineControl as TableListColumnFilterModel | undefined) as
              | GetRequestsDeadlineControlEnumKey[]
              | undefined;

            const { from: importedFrom, to: importedTo } = getDateRangeQuery(filterModel?.importedAt as DateRangePickerValue | undefined);
            const { from: processedFrom, to: processedTo } = getDateRangeQuery(filterModel?.processedAt as DateRangePickerValue | undefined);
            const { from: createdFrom, to: createdTo } = getDateRangeQuery(filterModel?.createdAt as DateRangePickerValue | undefined);
            const { from: updatedFrom, to: updatedTo } = getDateRangeQuery(filterModel?.updatedAt as DateRangePickerValue | undefined);
            const { from: reviewDueFrom, to: reviewDueTo } = getDateOnlyRangeQuery(filterModel?.reviewDueDate as DateRangePickerValue | undefined);
            const sortBy = sortColumn?.colId ? COLUMN_SORT_FIELD_MAP[sortColumn.colId] : undefined;

            const { rows, totalElements } = await fetchApplicationsTableRows({
              tableFilter,
              isExternalOperator: false,
              startRow,
              blockSize: size,
              fetchPage: async (fetchPage, fetchSize) => {
                const response = await getInternalRequests({
                  query: {
                    search: searchQuery,
                    page: fetchPage,
                    size: fetchSize,
                    status,
                    sortBy,
                    sortDir,
                    railway: railway && railway.length > 0 ? railway : undefined,
                    requestSubject: requestSubject && requestSubject.length > 0 ? requestSubject : undefined,
                    sections: sections.length > 0 ? sections : undefined,
                    applicantNames: applicantNames.length > 0 ? applicantNames : undefined,
                    initiatorIds: initiatorIds.length > 0 ? initiatorIds : undefined,
                    assigneeIds,
                    assigneeUnassigned,
                    operatorIds: operatorIds.length > 0 ? operatorIds : undefined,
                    paymentFlag: paymentFlag && paymentFlag.length > 0 ? paymentFlag : undefined,
                    processingFlag: processingFlag && processingFlag.length > 0 ? processingFlag : undefined,
                    deadlineControl: deadlineControl && deadlineControl.length > 0 ? deadlineControl : undefined,
                    importedFrom,
                    importedTo,
                    processedFrom,
                    processedTo,
                    createdFrom,
                    createdTo,
                    updatedFrom,
                    updatedTo,
                    reviewDueFrom,
                    reviewDueTo,
                    ...(monitoringMetric ? getMonitoringRequestFilters(monitoringMetric) : {}),
                    ...(monitoringMetric && scopedRailway ? { railway: getMonitoringRailwayQuery(scopedRailway) } : {}),
                  },
                  signal: abortController.signal,
                });
                const data = response.data;

                return {
                  content: (data.content ?? []) as ApplicationsTableItem[],
                  totalElements: toNumber(data.totalElements),
                };
              },
            });
            pageStore?.setTotalItemsCount(totalElements);
            successCallback(rows, totalElements);
            return;
          }

          if (clientType === "external") {
            const { rows, totalElements } = await fetchApplicationsTableRows({
              tableFilter,
              isExternalOperator,
              startRow,
              blockSize: size,
              rowMatcher: paymentRowMatcher,
              fetchPage: async (fetchPage, fetchSize) => {
                let response;

                if (isExternalOperator) {
                  const processingFlag = getSelectedListFilterIds(filterModel?.processingFlag as TableListColumnFilterModel | undefined) as
                    | GetOperatorRequestsProcessingFlagEnumKey[]
                    | undefined;
                  const operatorRailway = getSelectedListFilterIds(filterModel?.railway as TableListColumnFilterModel | undefined) as
                    | GetOperatorRequestsRailwayEnumKey[]
                    | undefined;
                  const operatorIds = getSelectedListFilterIds(filterModel?.operator as TableListColumnFilterModel | undefined);
                  const initiatorIds = getSelectedListFilterIds(filterModel?.initiator as TableListColumnFilterModel | undefined);
                  const { from: receivedFrom, to: receivedTo } = getDateRangeQuery(filterModel?.importedAt as DateRangePickerValue | undefined);
                  const { from: processedFrom, to: processedTo } = getDateRangeQuery(filterModel?.processedAt as DateRangePickerValue | undefined);
                  const sortBy = sortColumn?.colId ? EXTERNAL_OPERATOR_SORT_FIELD_MAP[sortColumn.colId] : undefined;

                  response = await getOperatorRequests({
                    query: {
                      search: searchQuery,
                      page: fetchPage,
                      size: fetchSize,
                      status: (columnStatusFilter ?? serverStatusFilter) as GetOperatorRequestsStatusEnumKey[] | undefined,
                      processingFlag: processingFlag && processingFlag.length > 0 ? processingFlag : undefined,
                      railway: operatorRailway && operatorRailway.length > 0 ? operatorRailway : undefined,
                      operatorId: operatorIds.length > 0 ? operatorIds : undefined,
                      initiatorId: initiatorIds.length > 0 ? initiatorIds : undefined,
                      receivedFrom,
                      receivedTo,
                      processedFrom,
                      processedTo,
                      sortBy,
                      sortDir,
                    },
                    signal: abortController.signal,
                  });
                } else {
                  const requestSubject = getSelectedListFilterIds(filterModel?.type as TableListColumnFilterModel | undefined) as
                    | ExternalGetRequestsRequestSubjectEnumKey[]
                    | undefined;
                  const applicantRailway = getSelectedListFilterIds(filterModel?.railway as TableListColumnFilterModel | undefined) as
                    | ExternalGetRequestsRailwayEnumKey[]
                    | undefined;
                  const applicantNames = getSelectedListFilterIds(filterModel?.applicantName as TableListColumnFilterModel | undefined);
                  const { from: createdFrom, to: createdTo } = getDateRangeQuery(filterModel?.createdAt as DateRangePickerValue | undefined);
                  const { from: updatedFrom, to: updatedTo } = getDateRangeQuery(filterModel?.updatedAt as DateRangePickerValue | undefined);
                  const sortBy = sortColumn?.colId ? EXTERNAL_APPLICANT_SORT_FIELD_MAP[sortColumn.colId] : undefined;

                  response = await getExternalRequests({
                    query: {
                      search: searchQuery,
                      page: fetchPage,
                      size: fetchSize,
                      status: (columnStatusFilter ?? serverStatusFilter) as GetRequestsStatusEnumKey[] | undefined,
                      requestSubject: requestSubject && requestSubject.length > 0 ? requestSubject : undefined,
                      railway: applicantRailway && applicantRailway.length > 0 ? applicantRailway : undefined,
                      applicantNames: applicantNames.length > 0 ? applicantNames : undefined,
                      createdFrom,
                      createdTo,
                      updatedFrom,
                      updatedTo,
                      sortBy,
                      sortDir,
                    },
                    signal: abortController.signal,
                  });
                }

                const data = response.data;

                return {
                  content: (data.content ?? []) as ApplicationsTableItem[],
                  totalElements: toNumber(data.totalElements),
                };
              },
            });
            pageStore?.setTotalItemsCount(totalElements);
            successCallback(rows, totalElements);
            return;
          }

          failCallback();
        } catch {
          failCallback();
        }
      },
      destroy: () => abortController.abort(),
    });
  }, [pageStore?.tableApi, search, pageStore?.tableFilter, clientType, userRoles, pageStore, monitoringMetric, scopedRailway]);
};
